import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, AbstractControl, Validators } from "@angular/forms";

//import { GridOptions, ColDef } from "@ag-grid-enterprise/all-modules";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { ApplicationsService } from "../../../../applications/services/applications.service";
import { AuthService } from "../../../../services/auth.service";
import { SidePanelService } from "../../../../common/services/side-panel.service";
import { ApplicationNotesService } from "../../../../applications/services/application-notes.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { WellService } from "../../../../common/services/well.service";
import { IUploadFile, DocumentManagementService } from '../../../../common/services/document-management.service';

import { SubHeader } from "../../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { WorkflowData, BulkNAD83Data, ApplicationState } from "../../../../applications/models/applications.models";
import { ParserError } from "../../../../builder/components/state/controls/config/data/file-parser-config/parser.model";


import { ApplicationBaseComponent } from "../../../../applications/components/application-base.component";

@Component({
    selector: 'app-nad83-readonly',
    templateUrl: '../approve-nad83-updates/approve-nad83-updates.component.html',
    standalone: false
})
export class NAD83ReadonlyComponent extends ApplicationBaseComponent implements OnInit, OnDestroy {

	private _data: BulkNAD83Data[];

	get data(): BulkNAD83Data[] {
		if (this._data != null) return this._data;
		if (this.form == null) return [];
		let ctrl = this.form.get('parsedData');
		if (ctrl == null || ctrl.value == null) return [];
		this._data = JSON.parse(ctrl.value);
		return this._data;
	}

	set data(value: BulkNAD83Data[]) {
		if (this.form == null) return;
		let ctrl = this.form.get('parsedData');
		if (ctrl == null) return;
		this._data = null;
		ctrl.setValue(JSON.stringify(value));
	}

	get dataColumns(): ColDef[] {
		return [
			{
				headerClass: "wrapped",
				headerName: "API #",
				headerTooltip: "API #",
				field: "apiNumber",
				tooltipField: "apiNumber",
				suppressMovable: true,
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				maxWidth: 125
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Latitude (NAD83)",
				headerTooltip: "Latitude (NAD83)",
				field: "latitude83",
				tooltipField: "latitude83",
				suppressMovable: true,
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Longitude (NAD83)",
				headerTooltip: "Longitude (NAD83)",
				field: "longitude83",
				tooltipField: "longitude83",
				suppressMovable: true,
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Source of Location",
				headerTooltip: "Source of Location",
				field: "sourceOfLocation",
				tooltipField: "sourceOfLocation",
				suppressMovable: true,
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams
			}

		] as ColDef[];
	}

	@ViewChild("downloadLink") public downloadLink: ElementRef;

	get errorGridColumns(): ColDef[] {
		return [
			{
				headerName: "Line",
				headerTooltip: "Line",
				field: "line",
				tooltipField: "line",
				suppressMovable: true,
				maxWidth: 100
			} as ColDef,
			{
				headerName: "Column",
				headerTooltip: "Column",
				field: "column",
				tooltipField: "column",
				suppressMovable: true,
				maxWidth: 125,
				minWidth: 125
			} as ColDef,
			{
				headerName: "Error",
				headerTooltip: "Error",
				field: "error",
				tooltipField: "error",
				suppressMovable: true
			} as ColDef
		];
	}

	errorGridOptions: GridOptions;
	errorGridApi?: GridApi;
	private _errors: ParserError[];

	get errors(): ParserError[] {
		if (this._errors != null) return this._errors;
		if (this.form == null) return [];
		let ctrl = this.form.get('errors');
		if (ctrl == null || ctrl.value == null) return [];
		this._errors = JSON.parse(ctrl.value);
		return this._errors;
	}

	set errors(value: ParserError[]) {
		if (this.form == null) return;
		let ctrl = this.form.get('errors');
		if (ctrl == null) return;
		this._errors = null;
		ctrl.setValue(JSON.stringify(value));
	}

	get fileCtrl(): AbstractControl {
		if (this.form == null) return null;
		return <AbstractControl>this.form.get("files");
	}

	get files(): IUploadFile[] {
		if (this.fileCtrl == null) return [];
		return <IUploadFile[]>this.fileCtrl.value;
	}

	get hasFileListing(): boolean {
		return this.files != null && this.files.length > 0;
	}

	inputStates: ApplicationState[] = [ApplicationState.Input, ApplicationState.Start, ApplicationState.Corrections];


	parsedGridOptions: GridOptions;
	parsedGridApi?: GridApi;
	statusBar = {
		statusPanels: [
			{
				statusPanel: 'agTotalAndFilteredRowCountComponent',
				align: 'right',
			}
		]
	};

	private subheader: SubHeader = new SubHeader({ title: "Bulk NAD83 Update - Application #[APPNUMBER]", buttons: [{ name: "Exit", method: () => this.home() }] });



	constructor(private route: ActivatedRoute, private _builder: FormBuilder, _router: Router, _subheaderService: SubHeaderService, _dialog: DynamicDialogService, _auth: AuthService, private _docService: DocumentManagementService,
		_loader: LoaderService, _applications: ApplicationsService, _notes: ApplicationNotesService, _sidePanel: SidePanelService, private _wells: WellService, private _formatter: GridFormatterService) {
		super(_loader, _dialog, _subheaderService, _auth, _applications, _router, _sidePanel, _notes);
		this.fileFields = ['files'];
	}

	ngOnInit() {
		
		super.ngOnInit();
		if (this.route.snapshot.data.application) {
			this.application = this.route.snapshot.data.application;
			if (this.inputStates.includes(this.application.applicationState)) { //application is not in review state; redirect to the input page
				let url = `/admin/bulk-nad83-update/${this.application.applicationNumber}`;
				this.router.navigate([url]);
			} else if (this.application.applicationState == ApplicationState.InitialReview) {
				let url = `/admin/bulk-nad83-update/review/${this.application.applicationNumber}`;
				this.router.navigate([url]);
			}
			this.subheader.title = this.subheader.title.replace("[APPNUMBER]", this.application.applicationNumber);
			this.subheaderService.updateSubHeader(this.subheader);
		}
		this.buildForm(this.application);
		this.setupGrids();

	}

	buildForm(data: WorkflowData | null = null): void {
		this.form = this._builder.group({
			files: [[], [Validators.required, Validators.minLength(1)]],
			parsedData: [null, [Validators.required]],
			errors: [null, []]
		});
		if (data != null) {
			this.form = this.restoreFormData(this.form);
		}
		this.formReady$.next(true);
	}

	setupGrids(): void {
		this.errorGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.errorGridColumns, false, true, true, true);
		this.parsedGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.dataColumns, false, true, true, true);
		this.parsedGridOptions.headerHeight = 50;
		this.parsedGridOptions.pagination = false;
		this.parsedGridOptions.onGridReady = (params) => {
			this.parsedGridApi = params.api;
			this.parsedGridApi.sizeColumnsToFit();
		};
		this.parsedGridOptions.onFirstDataRendered = () => {
			if (this.parsedGridApi != null) {
				this.parsedGridApi.sizeColumnsToFit();
			}
		}
		this.errorGridOptions.pagination = false;
		this.errorGridOptions.onGridReady = (params) => {
			this.errorGridApi = params.api;
		}
		this.errorGridOptions.onFirstDataRendered = () => { 
			if (this.errorGridApi != null) {
				this.errorGridApi.sizeColumnsToFit();
			}
		 }
	}

}
